import { Box, Snackbar, Alert, Typography } from "@mui/material";
import React, { useState, useEffect, useRef, useCallback } from "react";
import RfiNavSidebar from "./Components/RfiNavSidebar";
import WorkOrderForm from "../../Common/WorkOrderForm";
import RfiHeader from "../RfiHeader";
import theme from "../../../utils/theme";
import { useMediaQuery } from "@mui/material";
import { useRfi } from "../../../contexts/RfiContext";
import { useSelector } from "react-redux";
import { actionType, userRoles } from "../../../utils/constant";
import StageWrapper from "./Components/StageWrapper";
import ComponentViewAndNDTmap from "./Components/ComponentViewAndNDTMapping/ComponentViewAndNDTmap";
import AssemblyDocsAndVisualCheck from "./Components/AssemblyDocsAndVisualCheck/AssemblyDocsAndVisualCheck";
import Additions from "./Components/Additions/Additions";
import Blasting from "./Components/Blasting/Blasting";
import PaintCoatDetails from "./Components/PaintingCoats/PaintCoatDetails";
import { hasPermission } from "../../../utils/utilityFunctions";
import AppButton from "../../Common/AppButton";
import { useNavigate } from "react-router-dom";

// Consolidated styles object
const styles = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    position: "relative",
    height: "86vh",
    paddingTop: "64px",
    background: theme.palette.background.default,
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  scrollContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    ml: 4,
    overflowY: "auto",
    paddingRight: 2,
    transition: "margin-left 0.3s ease",
    willChange: "transform",
    WebkitOverflowScrolling: "touch",
    "& .disabled-stage": {
      opacity: 0.6,
      pointerEvents: "none",
    },
  },
  scrollContainerSidebarCollapsed: {
    ml: 0,
  },
  alert: {
    width: "100%",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  nonPermissionContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "75vh",
    flexDirection: "column",
  },
};

const ViewRfi = ({ rfiDetails }) => {
  const { stagesDetails } = useRfi();
  const { profile } = useSelector((state) => state.profile);
  const navigate = useNavigate();

  // Find the active stage
  const activeStage = stagesDetails?.find(
    (stage) =>
      (stage.stage_number === "Stage_7" &&
        profile?.role === userRoles.Product_Engineer &&
        stage.status === "Pending") ||
      (stage.is_active && profile?.role !== userRoles.Product_Engineer)
  );

  // Stage management state
  const [currentStageId, setCurrentStageId] = useState(
    activeStage?.stage_id || stagesDetails?.[0]?.stage_id
  );
  const [showWarning, setShowWarning] = useState(false);
  const [hasScrolledOnLoad, setHasScrolledOnLoad] = useState(false);

  const scrollContainerRef = useRef(null);
  const isScrollingProgrammatically = useRef(false);
  const lastScrollTime = useRef(0);
  const isGreaterThanMd = useMediaQuery(theme.breakpoints.up("lg"));

  // Other state
  const [collapsibleOpen, setCollapsibleOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(isGreaterThanMd);

  // Check if a stage is allowed to be viewed
  const isStageAllowed = useCallback(
    (stageId) => {
      // Find the stage in the details
      const stage = stagesDetails?.find((s) => s.stage_id === stageId);
      if (!stage) return false;

      // Allow if stage is completed or active
      return (
        stage.status === "Completed" ||
        stage.is_active ||
        profile?.role === userRoles.Product_Engineer
      );
    },
    [stagesDetails, profile?.role]
  );

  // Handle stage selection from sidebar
  const handleStageSelect = React.useCallback(
    (stageId) => {
      // Only allow navigation to stages that are allowed
      if (isStageAllowed(stageId)) {
        isScrollingProgrammatically.current = true;

        // Scroll to the selected stage
        const stageElement = document.getElementById(`stage-${stageId}`);
        if (stageElement && scrollContainerRef.current) {
          // Calculate position manually for smoother scrolling
          const containerRect =
            scrollContainerRef.current.getBoundingClientRect();
          const elementRect = stageElement.getBoundingClientRect();
          const scrollTop =
            elementRect.top -
            containerRect.top +
            scrollContainerRef.current.scrollTop;

          // Use requestAnimationFrame for smoother scrolling
          requestAnimationFrame(() => {
            scrollContainerRef.current.scrollTo({
              top: scrollTop,
              behavior: "smooth",
            });
          });

          // Update the current stage immediately for better UI feedback
          setCurrentStageId(stageId);
        }

        // Reset the flag after a short delay
        setTimeout(() => {
          isScrollingProgrammatically.current = false;
        }, 500);
      } else {
        setShowWarning(true);
      }
    },
    [isStageAllowed]
  );

  // Scroll to active stage on first load
  useEffect(() => {
    if (!hasScrolledOnLoad && scrollContainerRef.current && activeStage) {
      const scrollToActiveStage = () => {
        const stageElement = document.getElementById(
          `stage-${activeStage.stage_id}`
        );
        if (stageElement && scrollContainerRef.current) {
          isScrollingProgrammatically.current = true;

          const containerRect =
            scrollContainerRef.current.getBoundingClientRect();
          const elementRect = stageElement.getBoundingClientRect();
          const scrollTop =
            elementRect.top -
            containerRect.top +
            scrollContainerRef.current.scrollTop;

          scrollContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: "smooth",
          });

          setTimeout(() => {
            isScrollingProgrammatically.current = false;
            setHasScrolledOnLoad(true);
          }, 500);
        }
      };

      // Add a small delay to ensure DOM is fully rendered
      const timeoutId = setTimeout(scrollToActiveStage, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [activeStage, hasScrolledOnLoad]);

  // Handle manual scroll events
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Ignore programmatic scrolling
      if (isScrollingProgrammatically.current) return;

      const now = Date.now();
      // Throttle scroll events to improve performance
      if (now - lastScrollTime.current < 100) return;
      lastScrollTime.current = now;

      // Get current scroll position
      const scrollTop = scrollContainer.scrollTop;
      const containerHeight = scrollContainer.clientHeight;

      // Find which stage is currently in view
      let currentVisibleStageId = null;

      stagesDetails?.forEach((stage) => {
        const stageElement = document.getElementById(`stage-${stage.stage_id}`);
        if (!stageElement) return;

        const stageTop = stageElement.offsetTop - scrollContainer.offsetTop;
        const stageBottom = stageTop + stageElement.offsetHeight;

        // Check if stage is in view
        if (
          scrollTop >= stageTop - 50 &&
          scrollTop <= stageBottom - containerHeight / 2
        ) {
          currentVisibleStageId = stage.stage_id;
        }
      });

      // If we found a stage and it's not allowed, prevent scrolling to it
      if (currentVisibleStageId && !isStageAllowed(currentVisibleStageId)) {
        setShowWarning(true);

        // Scroll back to the last allowed stage
        isScrollingProgrammatically.current = true;
        const allowedStages = stagesDetails?.filter(
          (stage) => stage.status === "Completed" || stage.is_active
        );

        if (allowedStages.length > 0) {
          const lastAllowedStage = allowedStages[allowedStages.length - 1];
          scrollContainer.scrollTo({
            top:
              document.getElementById(`stage-${lastAllowedStage.stage_id}`)
                .offsetTop - scrollContainer.offsetTop,
            behavior: "smooth",
          });
        }

        setTimeout(() => {
          isScrollingProgrammatically.current = false;
        }, 500);
      } else if (
        currentVisibleStageId &&
        currentVisibleStageId !== currentStageId
      ) {
        // Update current stage if it's changed and allowed
        setCurrentStageId(currentVisibleStageId);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [currentStageId, isStageAllowed, stagesDetails]);

  if (
    !hasPermission(profile?.permissions?.actions, actionType.rfi_view) ||
    !hasPermission(profile?.permissions?.actions, actionType.stage_view)
  ) {
    return (
      <Box sx={styles.nonPermissionContainer}>
        <Typography variant="h5" mb={3}>
          You don't have permission to view RFI.
        </Typography>
        <AppButton
          label="Back to Dashboard"
          onClick={() => navigate("/dashboard")}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.mainContainer}>
      <RfiHeader
        rfiDetails={rfiDetails}
        stagesDetails={stagesDetails}
        role={profile?.role}
        actions={profile?.permissions?.actions}
      />
      <WorkOrderForm
        fields={rfiDetails}
        editable={false}
        collapsibleOpen={collapsibleOpen}
        setCollapsibleOpen={setCollapsibleOpen}
      />

      {stagesDetails?.length > 0 ? (
        <Box sx={styles.contentContainer}>
          {/* RFI Navigation Sidebar */}
          <RfiNavSidebar
            stageDetails={stagesDetails}
            onStageSelect={handleStageSelect}
            open={sidebarExpanded}
            onToggleDrawer={() => setSidebarExpanded(!sidebarExpanded)}
            currentStageId={currentStageId}
            profile={profile}
          />

          {/* Main content - Scrollable */}
          <Box
            ref={scrollContainerRef}
            sx={{
              ...styles.scrollContainer,
              ...(!sidebarExpanded && styles.scrollContainerSidebarCollapsed),
            }}
          >
            {/* Render all stages from the API */}
            {stagesDetails.map((stage) => (
              <Box
                key={stage.stage_id}
                id={`stage-${stage.stage_id}`}
                className={
                  !isStageAllowed(stage.stage_id) ? "disabled-stage" : ""
                }
              >
                {getStageComponent(stage, profile, isStageAllowed, rfiDetails)}
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Typography variant="h5" sx={{ color: theme.palette.error.main }}>
            No Data Found for stages
          </Typography>
        </Box>
      )}

      {/* Warning Snackbar for unauthorized stage access */}
      <Snackbar
        open={showWarning}
        autoHideDuration={3000}
        onClose={() => setShowWarning(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowWarning(false)}
          severity="warning"
          sx={styles.alert}
        >
          {profile?.role === userRoles.Quality_Controller_Inspector
            ? "Please complete the previous stages before accessing this stage."
            : "Below stages details are not filled yet."}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Helper function to get the appropriate stage component
const getStageComponent = (stage, profile, isStageAllowed, rfiDetails) => {
  // Data or footers
  const stageNumber = stage.stage_number;
  const isQCCoordinator =
    profile?.role === userRoles.Quality_Controller_Coordinator;
  const isQCInspector =
    profile?.role === userRoles.Quality_Controller_Inspector;
  const isQCManager = profile?.role === userRoles.Quality_Controller_Manager;
  const isAdmin = profile?.role === userRoles.Admin;
  const isProductEngineer = profile?.role === userRoles.Product_Engineer;

  const QCManagerApproved = stage.manager_approved;
  const QCCoordinatorApproved = stage.coordinator_approved;
  const isActive = stage.is_active || stage.status === "Completed";

  const formEnabled =
    (stage.is_active && stage.current_step === "Inspector" && isQCInspector) ||
    (stage.stage_number === "Stage_7" &&
      isProductEngineer &&
      !rfiDetails.is_addition_added) ||
    profile?.role === userRoles.Admin;
  const showAction =
    ((stage.is_active && !isProductEngineer) ||
      (stage.stage_number === "Stage_7" &&
        isProductEngineer &&
        !rfiDetails.is_addition_added)) &&
    stage.status !== "Completed" &&
    ((isQCCoordinator &&
      !QCCoordinatorApproved &&
      stage.current_step === "Coordinator") ||
      (isQCManager && !QCManagerApproved && stage.current_step === "Manager") ||
      (isQCInspector && stage.current_step === "Inspector") ||
      (isProductEngineer && stage.stage_number === "Stage_7") ||
      isAdmin);
  const showSubmitButton =
    (stage.current_step === "Inspector" &&
      profile.role === userRoles.Quality_Controller_Inspector) ||
    (stage.stage_number === "Stage_7" &&
      isProductEngineer &&
      !rfiDetails.is_addition_added);
  const awaitingReview =
    stage.status === "In_Progress" &&
    (stage.current_step === "Coordinator" || stage.current_step === "Manager");

  const isAPICallAllowed = isStageAllowed(stage.stage_id);

  const componentProps = {
    formEnabled,
    showAction,
    showSubmitButton,
    awaitingReview,
    isQCCoordinator,
    stage,
    isAPICallAllowed,
    isActive,
  };

  const stageComponents = {
    Stage_1: (
      <StageWrapper stageID={1} stageTitle="Component View & NDT Mapping">
        <ComponentViewAndNDTmap {...componentProps} />
      </StageWrapper>
    ),
    Stage_2: (
      <StageWrapper stageID={2} stageTitle="Assembly">
        <AssemblyDocsAndVisualCheck {...componentProps} />
      </StageWrapper>
    ),
    Stage_3: (
      <StageWrapper stageID={3} stageTitle="Blasting">
        <Blasting {...componentProps} />
      </StageWrapper>
    ),
    Stage_4: (
      <StageWrapper stageID={4} stageTitle="Primer Coat">
        <PaintCoatDetails coatType="primer" {...componentProps} />
      </StageWrapper>
    ),
    Stage_5: (
      <StageWrapper stageID={5} stageTitle="Second Coat">
        <PaintCoatDetails coatType="second" {...componentProps} />
      </StageWrapper>
    ),
    Stage_6: (
      <StageWrapper stageID={6} stageTitle="Top Coat">
        <PaintCoatDetails coatType="top" {...componentProps} />
      </StageWrapper>
    ),
    Stage_7: (
      <StageWrapper stageID={7} stageTitle="Additions">
        <Additions
          {...componentProps}
          showInspectorFields={isQCInspector}
          isProductEngineer={isProductEngineer}
          isAdmin={isAdmin}
          showAllColumns={rfiDetails.is_addition_added}
        />
      </StageWrapper>
    ),
  };

  return stageComponents[stageNumber] || <div>Unknown Stage</div>;
};

export default ViewRfi;
